/**
 * Camada de acesso a dados do Gingado (repositórios). Leituras pensadas para
 * `useLiveQuery`; escritas respeitam as regras da spec: origem gravada sempre,
 * momentos da criança nascem pendentes (confirmado=false).
 *
 * Nota sobre `confirmado`: o IndexedDB não indexa booleanos, então filtramos
 * pendências em memória (a base local é pequena).
 */
import { db } from "../schema.dexie";
import { momentoDaCrianca } from "./domain";
import type {
  AtividadePsicomotora,
  ContextoMomento,
  DemandaRelacional,
  EstadoRegulacao,
  MomentoPsicomotor,
  NivelAjuda,
  PassoParticipacao,
  PerfilPsicomotor,
  StatusAtividade,
  TipoAtividade,
} from "./types";

const novoId = () => crypto.randomUUID();
const agoraISO = () => new Date().toISOString();

const PSICOMOTOR_VAZIO: PerfilPsicomotor = {
  fator_alvo: null,
  sistema: [],
  intensidade: null,
  previsibilidade: null,
};

// ── Leituras (use com useLiveQuery) ─────────────────────────────────────────

/** Atividades não arquivadas, ordenadas por nome. */
export const listarAtividades = (incluirArquivadas = false): Promise<AtividadePsicomotora[]> =>
  db.atividade
    .orderBy("nome")
    .filter((a) => incluirArquivadas || !a.arquivado)
    .toArray();

export const getAtividade = (id: string) => db.atividade.get(id);

export const getTodosMomentos = (): Promise<MomentoPsicomotor[]> => db.momento_psm.toArray();

export const getMomentosDaAtividade = (atividadeId: string): Promise<MomentoPsicomotor[]> =>
  db.momento_psm
    .where("[atividade_id+data_hora]")
    .between([atividadeId, ""], [atividadeId, "￿"])
    .reverse()
    .toArray();

/** Momentos pendentes de confirmação (origem criança). Filtra em memória. */
export const getMomentosPendentes = (): Promise<MomentoPsicomotor[]> =>
  db.momento_psm.filter((m) => m.confirmado === false).toArray();

// ── Biblioteca (P0.1) ────────────────────────────────────────────────────────

export type NovaAtividadeInput = {
  nome: string;
  tipo: TipoAtividade;
  psicomotor?: Partial<PerfilPsicomotor>;
  demanda_relacional?: DemandaRelacional | null;
  status?: StatusAtividade;
  seguranca?: string | null;
  observacoes?: string | null;
  midias?: string[];
};

export async function criarAtividade(input: NovaAtividadeInput): Promise<string> {
  const ativ: AtividadePsicomotora = {
    id: novoId(),
    nome: input.nome.trim(),
    tipo: input.tipo,
    midias: input.midias ?? [],
    psicomotor: { ...PSICOMOTOR_VAZIO, ...input.psicomotor },
    demanda_relacional: input.demanda_relacional ?? null,
    status: input.status ?? "nao_usada",
    seguranca: input.seguranca ?? null,
    observacoes: input.observacoes ?? null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.atividade.add(ativ);
  return ativ.id;
}

export const editarAtividade = (id: string, patch: Partial<AtividadePsicomotora>) =>
  db.atividade.update(id, patch);

export const definirStatusAtividade = (id: string, status: StatusAtividade) =>
  db.atividade.update(id, { status });

/** P0.1 — arquivar em vez de deletar. */
export const arquivarAtividade = (id: string) =>
  db.atividade.update(id, { arquivado: true });

// ── Momentos (P0.2 / P0.3) ────────────────────────────────────────────────────

export type NovoMomentoInput = {
  atividade_id: string;
  crianca_id: string;
  passo_participacao: PassoParticipacao;
  nivel_ajuda?: NivelAjuda | null;
  estado_antes?: EstadoRegulacao | null;
  estado_depois?: EstadoRegulacao | null;
  duracao_min?: number | null;
  contexto?: ContextoMomento | null;
  com_quem?: string[];
  notas?: string | null;
};

/** P0.2 — registro do cuidador: origem fixa, confirmado, em ≤10s. */
export async function registrarMomentoCuidador(input: NovoMomentoInput): Promise<string> {
  const momento: MomentoPsicomotor = {
    id: novoId(),
    data_hora: agoraISO(),
    nivel_ajuda: null,
    estado_antes: null,
    estado_depois: null,
    duracao_min: null,
    contexto: null,
    notas: null,
    ...input,
    com_quem: input.com_quem ?? [],
    origem: "cuidador",
    confirmado: true,
  };
  await db.momento_psm.add(momento);
  return momento.id;
}

/** P0.3 — escolha da criança: origem `crianca`, nasce pendente (confirmado=false). */
export async function registrarEscolhaCrianca(
  atividadeId: string,
  criancaId: string,
  passo: PassoParticipacao,
  contexto: ContextoMomento | null = null,
): Promise<string> {
  const base = {
    id: novoId(),
    atividade_id: atividadeId,
    crianca_id: criancaId,
    data_hora: agoraISO(),
    passo_participacao: passo,
    nivel_ajuda: null,
    estado_antes: null,
    estado_depois: null,
    duracao_min: null,
    contexto,
    com_quem: [] as string[],
    notas: "escolheu no modo escolha",
  };
  const momento = momentoDaCrianca(base);
  await db.momento_psm.add(momento);
  return momento.id;
}

export const confirmarMomento = (id: string) =>
  db.momento_psm.update(id, { confirmado: true });

/** Confirma uma escolha da criança e ajusta o passo de participação observado. */
export const confirmarMomentoComPasso = (id: string, passo: PassoParticipacao) =>
  db.momento_psm.update(id, { confirmado: true, passo_participacao: passo });

export const descartarMomento = (id: string) => db.momento_psm.delete(id);
